import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, FolderTree, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];
const CASH_FLOW = { CASH: "Cash & bank", OPERATING: "Operating", INVESTING: "Investing", FINANCING: "Financing" };

/** Accounts in tree order (group, then its children), each with its depth. */
function treeOrder(accounts) {
  const children = {};
  accounts.forEach((a) => {
    const key = a.parent_id || "root";
    if (!children[key]) children[key] = [];
    children[key].push(a);
  });
  Object.values(children).forEach((list) => list.sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true })));
  const out = [];
  const walk = (key, depth) => (children[key] || []).forEach((a) => { out.push({ ...a, depth }); walk(a.id, depth + 1); });
  walk("root", 0);
  const seen = new Set(out.map((a) => a.id));
  accounts.filter((a) => !seen.has(a.id)).forEach((a) => out.push({ ...a, depth: 0 }));
  return out;
}

function effectiveCashFlow(account, byId) {
  let node = account;
  while (node) {
    if (node.cash_flow) return { value: node.cash_flow, inherited: node.id !== account.id };
    node = node.parent_id ? byId[node.parent_id] : null;
  }
  return { value: null, inherited: false };
}

export default function ChartOfAccounts() {
  const { can } = usePermissions();
  const canCreate = can("CHART_OF_ACCOUNTS", "create");
  const canEdit = can("CHART_OF_ACCOUNTS", "edit");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedAcct, setSelectedAcct] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gl/accounts");
      setAccounts(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const allRows = useMemo(() => treeOrder(accounts), [accounts]);
  const rows = useMemo(() => {
    if (!search) return allRows;
    const q = search.toLowerCase();
    return allRows.filter(a =>
      (a.code || "").toLowerCase().includes(q) ||
      (a.name || "").toLowerCase().includes(q) ||
      (a.account_type || "").toLowerCase().includes(q)
    );
  }, [allRows, search]);

  const toggleActive = async (account) => {
    try {
      await api.patch(`/gl/accounts/${account.id}`, { is_active: !account.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-page">
      <PageHeader
        title="Chart of Accounts"
        description="Groups (bold) collect the accounts under them. Journals post only to ordinary accounts; reports can show group totals."
        action={canCreate && <Button onClick={() => setDialog({ mode: "new" })} data-testid="create-account-button"><Plus className="h-4 w-4" /> New Account</Button>}
      />
      <PageStatCards loading={loading} stats={[
        { label: "Total Accounts", value: accounts.length, color: "blue" },
        { label: "Active",         value: accounts.filter(a => a.is_active).length, color: "green" },
        { label: "Groups",         value: accounts.filter(a => a.is_group).length, color: "orange" },
        { label: "Inactive",       value: accounts.filter(a => !a.is_active).length, color: "gray" },
      ]} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={rows.map(a => ({
          Code: a.code, Name: a.name, Type: a.account_type,
          Group: a.is_group ? "Yes" : "No", Status: a.is_active ? "Active" : "Inactive",
        }))}
        csvFilename="chart-of-accounts.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && accounts.length === 0 ? (
          <EmptyState title="No accounts yet" testId="accounts-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Cash flow</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => {
                const cf = effectiveCashFlow(a, byId);
                return (
                  <TableRow key={a.id} data-testid={`account-row-${a.code}`}>
                    <TableCell className="font-mono text-xs text-foreground">{a.code}</TableCell>
                    <TableCell className="text-foreground" style={{ paddingLeft: `${a.depth * 22 + 16}px` }}>
                      {a.is_group ? <span className="inline-flex items-center gap-1.5 font-semibold"><FolderTree className="h-3.5 w-3.5 text-muted-foreground" />{a.name}</span> : a.name}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.account_type}</TableCell>
                    <TableCell className="text-xs">
                      {cf.value ? <span className={cf.inherited ? "text-muted-foreground" : "text-foreground"}>{CASH_FLOW[cf.value]}{cf.inherited ? " (from group)" : ""}</span> : <span className="text-muted-foreground">Auto</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={a.is_active} disabled={!canEdit} onCheckedChange={() => toggleActive(a)} data-testid={`account-active-toggle-${a.code}`} />
                        <Badge variant={a.is_active ? "default" : "secondary"}>{a.is_active ? "Active" : "Inactive"}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setSelectedAcct(a)}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                          title="View"
                          aria-label="View"
                          data-testid={`view-account-${a.code}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {canEdit && <Button size="sm" variant="ghost" onClick={() => setDialog({ mode: "edit", account: a })} data-testid={`edit-account-${a.code}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {dialog && <AccountDialog key={dialog.account?.id || "new"} mode={dialog.mode} account={dialog.account} accounts={accounts} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}

      <DetailDrawer
        open={!!selectedAcct}
        onClose={() => setSelectedAcct(null)}
        title={selectedAcct?.name}
        subtitle={`${selectedAcct?.account_type} · ${selectedAcct?.code}`}
      >
        <DrawerSection label="Account Details">
          <DrawerField label="Code" value={selectedAcct?.code} mono />
          <DrawerField label="Name" value={selectedAcct?.name} />
          <DrawerField label="Type" value={selectedAcct?.account_type} />
          <DrawerField label="Group Account" value={selectedAcct?.is_group ? "Yes" : "No"} />
          <DrawerField label="Cash Flow Category" value={selectedAcct?.cash_flow ? CASH_FLOW[selectedAcct.cash_flow] : "Auto"} />
          <DrawerField label="Status" value={selectedAcct?.is_active ? "Active" : "Inactive"} />
        </DrawerSection>
      </DetailDrawer>
    </div>
  );
}

function AccountDialog({ mode, account, accounts, onClose, onSaved }) {
  const editing = mode === "edit";
  const [form, setForm] = useState(editing
    ? { code: account.code, name: account.name, account_type: account.account_type, parent_id: account.parent_id || "", is_group: account.is_group, cash_flow: account.cash_flow || "" }
    : { code: "", name: "", account_type: "EXPENSE", parent_id: "", is_group: false, cash_flow: "" });
  const [suggested, setSuggested] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const parents = accounts.filter((a) => a.is_group && a.account_type === form.account_type && a.id !== account?.id);
  useEffect(() => {
    if (editing) return;
    const root = parents.find((p) => !p.parent_id);
    if (!form.parent_id && root) setForm((f) => ({ ...f, parent_id: root.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.account_type]);
  useEffect(() => {
    if (editing) return;
    api.get("/codes/suggest", { params: { kind: "account", account_type: form.account_type, parent_id: form.parent_id || undefined } })
      .then(({ data }) => setSuggested(data.code)).catch(() => setSuggested(""));
  }, [editing, form.account_type, form.parent_id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      if (editing) {
        await api.patch(`/gl/accounts/${account.id}`, { name: form.name, parent_id: form.parent_id || null, is_group: form.is_group, cash_flow: form.cash_flow || null });
        toast.success(`${account.code} updated`);
      } else {
        const { data } = await api.post("/gl/accounts", { ...form, code: form.code.trim() || null, parent_id: form.parent_id || null, cash_flow: form.cash_flow || null });
        toast.success(`${data.code} ${data.name} created`);
      }
      onSaved?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${account.code}` : "New Account"}</DialogTitle>
          <DialogDescription>Choose a group of the same type as the parent. Turn on "Group" to create a heading that other accounts go under.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5" data-testid="account-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input disabled={editing} data-testid="account-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder={suggested || "Automatic if blank"} />
              {!editing && <p className="mt-0.5 text-[10px] text-muted-foreground">Blank = {suggested || "next number"}</p>}
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Type</Label>
              <Select disabled={editing} value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v, parent_id: "" })}>
                <SelectTrigger className="mt-1.5" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Name</Label><Input required data-testid="account-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="e.g. Site Security Charges" /><FieldError error={errors.name} /></div>
          <div>
            <Label>Parent group</Label>
            <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="account-parent-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (top level)</SelectItem>
                {parents.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
            <span><span className="font-medium text-foreground">Group account</span><span className="block text-xs text-muted-foreground">A heading only — no entries post to it</span></span>
            <Switch checked={form.is_group} onCheckedChange={(v) => setForm({ ...form, is_group: v })} data-testid="account-group-switch" />
          </label>
          <div>
            <Label>Cash flow section</Label>
            <Select value={form.cash_flow || "auto"} onValueChange={(v) => setForm({ ...form, cash_flow: v === "auto" ? "" : v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (from the group, else by type)</SelectItem>
                <SelectItem value="CASH">Cash &amp; bank — this IS a cash / bank account</SelectItem>
                <SelectItem value="OPERATING">Operating</SelectItem>
                <SelectItem value="INVESTING">Investing (assets)</SelectItem>
                <SelectItem value="FINANCING">Financing (capital, loans)</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">Set it on a group and every account under it follows. Mark every bank account as "Cash &amp; bank".</p>
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="account-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
